/*
 *  Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
 *  is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
 *  and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
 *
 *  You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
 *  in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
 *  forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
 *  explicitly covering such access.
 *
 *  The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
 *  which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
 *
 *  ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
 *  CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
 *  INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
 *  OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
 *  THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.
 *
 */

import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import './device-detail.less'
import {
  BarChartOutlined,
  CameraOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  ForkOutlined,
  StarOutlined,
  UploadOutlined,
  UserOutlined
} from '@ant-design/icons'
import { DeviceType, EnvironmentsOptions, IDevice } from '../../constants/device'
import { CombinedSettings, ILastSetting } from '../../api/setting'
import { Anchor, Col, Modal, Row, Typography } from 'antd'
import { DetailCard } from '../detail-card'
import {
  AIModelCell,
  CameraTypeCell,
  CaptureIntervalCell,
  DataSaveModeCell,
  ImageCompressionCell,
  IntervalTypeCell,
  OnlineHoursCell,
  OnlineStatusCell,
  PowerSavingModeCell,
  RecordingHoursCell,
  SettingSyncedCell,
  TaskTypeCell,
  TestModeCell,
  UploadFormatCell,
  UploadMethodCell,
  UploadQualityCell
} from '../camera-details'
import HealthTag from '../HealthTag'
import moment from 'moment'
import { ComposedTitle } from '../composed-title'
import { DataUsageCell } from '../camera-directory/DataUsageCell'
import { BatteryLevelCell } from '../camera-directory/BatteryLevelCell'
import { formatDayInMonth } from '../../utils/time'
import MaskLoading from 'components/MaskLoading'

interface IOption {
  label: string,
  value: number | string
}

const getLabelByValue = (value: string | number, options: IOption[]) => {
  const target = options.find(item => item.value === value)
  return target ? target.label : 'unknown'
}
const DeviceDetail = props => {
  const { visible, onChange } = props
  const mainNode = useRef(null)
  const containerStyle = classNames('device-detail', { 'show': visible })

  const handleUpload = ()=>{
    const { data = {} } = props
    const { directory, cityMap } = data
    const { detail, detailLoading } = directory
    const device: IDevice = detail.device
    const action = {type: 'upload',payload:{...device}}
    onChange(action)
  }

  const handleClose = ()=> {
    const action = {type: 'close'}
    onChange(action)
  }

  const Header = () => {
    const { data = {} } = props
    const { directory} = data
    const { detail} = directory
    const device: IDevice = detail.device
    const uploadStyle = classNames('upload',{hide: device?.deviceType === DeviceType.Lite360Slave})
    return (
      <div className={'header'}>
        <UploadOutlined className={uploadStyle} onClick={handleUpload}/>
        <CloseOutlined  onClick={handleClose}/>
      </div>
    )
  }

  const Content = () => {
    const { data = {} } = props
    const { directory, cityMap } = data
    const { detail, detailLoading } = directory
    const device: IDevice = detail.device
    const setting: CombinedSettings = detail.setting
    const lastSetting: ILastSetting = detail.lastSetting
    if (detailLoading) {
      return <MaskLoading />
    }

    if (!device || !setting || !lastSetting) {
      return null
    }
    const detailInfo = {
      deviceID: device.deviceID,
      onlineStatus: <OnlineStatusCell onlineStatus={device.onlineStatus} />,
      status: <HealthTag status={device.status} />,
      settingSynced: <SettingSyncedCell settingSynced={device.settingSynced} />,
      lastOnline: device.onlineTime === 0 ? '---' : moment.unix(device.onlineTime).format('MMM Do YYYY, h:mm A'),
      errorsLast7Days: device.errorsLast7Days,
      onlinePeriodPerday: <OnlineHoursCell onlineMinutes={device.onlinePeriodPerday} />,
      dataUsage: (
        <DataUsageCell
          quotaUsage={device.quotaUsage}
          quotaUsageLevel={device.quotaUsageLevel}
          quotaUsagePercent={device.quotaUsagePercent}
        />
      ),
      city: cityMap[device.cityID]?.name,
      cameraType: <CameraTypeCell type={device.deviceType} />,
      taskType: <TaskTypeCell taskType={device.taskType} />,
      testMode: <TestModeCell testMode={device.testMode} />,
      batteryLevel: <BatteryLevelCell battery={device.battery} />,
      powerSave: lastSetting.Id === 0 ? <span>---</span> : <PowerSavingModeCell powerSave={lastSetting.PowerSave} />,
      firmwareVersion: device.firmwareVersion === '' ? '---' : device.firmwareVersion,
      recordingHours: lastSetting.Id === 0
        ? <span>---</span>
        : <RecordingHoursCell workTimeStart={lastSetting.WorkTimeStart} workTimeEnd={lastSetting.WorkTimeEnd} />,
      ccsMode: lastSetting.Id === 0 ? <span>---</span> : <IntervalTypeCell intervalType={lastSetting.IntervalType} />,
      ccSInterval: lastSetting.Id === 0
        ? <span>---</span>
        : (
          <CaptureIntervalCell
            intervalType={lastSetting.IntervalType}
            captureInterval={lastSetting.CaptureInterval}
            captureDistanceInterval={lastSetting.CaptureDistanceInterval}
          />
        ),
      uploadQuality: lastSetting.Id === 0
        ? <span>---</span>
        : (
          <UploadQualityCell
            uploadFmt={lastSetting.UploadFmt}
            imageryResolution={lastSetting.ImageryResolution}
            videoResolution={lastSetting.VideoResolution}
          />
        ),
      imageQuality: lastSetting.Id === 0
        ? <span>---</span>
        : <ImageCompressionCell imageCompressRatio={lastSetting.ImageCompressRatio} />,
      totalOnlinePeriod: <OnlineHoursCell onlineMinutes={device.totalOnlinePeriod} />,
      //environment: getLabelByValue(device.environment, EnvironmentsOptions),
      uploadFormat: lastSetting.Id === 0 ? <span>---</span> : <UploadFormatCell uploadFormat={lastSetting.UploadFmt} />,
      driverID: device.driverID ? device.driverID : '---',
      imageQualityOfAiModel: lastSetting.Id === 0 ? <span>---</span> :
        <AIModelCell enabled={lastSetting.AiImageQuality} />,
      monthlyDataQuota: `${setting.trafficQuota} GB`,
      dataManagementCycle: `${device.billingCycleStart}${formatDayInMonth(device.billingCycleStart)} of the month`,
      dataAlertLimit: `${setting.alertThreshold}%`,
      dataHardLimit: `${setting.hardThreshold}%`,
      privacy: lastSetting.Id === 0 ? <span>{'---'}</span> : <AIModelCell enabled={lastSetting.AiPrivacy} />,
      uploadMethod: lastSetting.Id === 0 ? <span>---</span> :
        <UploadMethodCell uploadMethod={lastSetting.UploadMethod} />,
      dataSaverMode: lastSetting.Id === 0 ? <span>---</span> :
        <DataSaveModeCell imageryFormat={lastSetting.ImageryFmt} />

    }

    return (
      <>
        <div className='deviceID'>
          <Typography.Title level={3}>{device.deviceID}</Typography.Title>
        </div>
        <Anchor
          className='horizontal-anchor'
          affix={false}
          getContainer={() => mainNode.current || window}
          offsetTop={16}
        >
          <Anchor.Link href='#usageSnapshot' title={<StarOutlined />} />
          <Anchor.Link href='#camera' title={<CameraOutlined />} />
          <Anchor.Link href='#upload' title={<CloudUploadOutlined />} />
          <Anchor.Link href='#user' title={<UserOutlined />} />
          <Anchor.Link href='#aiModels' title={<ForkOutlined />} />
          <Anchor.Link href='#dataQuota' title={<BarChartOutlined />} />
        </Anchor>
        <div className='main' ref={mainNode}>
          <DetailCard icon={<StarOutlined />} title='Usage Snapshot' id='usageSnapshot' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Status
                </Col>
                <Col span={12}>
                  {detailInfo.onlineStatus}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Health Track
                </Col>
                <Col span={12}>
                  {detailInfo.status}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Setting Synced
                </Col>
                {detailInfo.settingSynced}
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Last Online
                </Col>
                <Col span={12}>
                  {detailInfo.lastOnline}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='# of Errors' secondary='Total past 7 days' />
                </Col>
                <Col span={12}>{detailInfo.errorsLast7Days}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Online Time' secondary='Avg past 7 days' />
                </Col>
                <Col span={12}>
                  {detailInfo.onlinePeriodPerday}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Data Usage' secondary='Total this month' />
                </Col>
                <Col span={12}>
                  {detailInfo.dataUsage}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Unique KM' secondary='Avg past 7 days' />
                </Col>
                <Col span={12}>{device.uniqueDistPerday}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Battery Level
                </Col>
                <Col span={12}>
                  {detailInfo.batteryLevel}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Distance Travelled' secondary='Total all time' />
                </Col>
                <Col span={12}>{device.totalDistanceCovered} km</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  <ComposedTitle primary='Online Time' secondary='Total all time' />
                </Col>
                <Col span={12}>
                  {detailInfo.totalOnlinePeriod}
                </Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <DetailCard icon={<CameraOutlined />} title='Camera' id='camera' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  CameraID
                </Col>
                <Col span={12}>{detailInfo.deviceID}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera Type
                </Col>
                <Col span={12}>
                  {detailInfo.cameraType}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera City
                </Col>
                <Col span={12}>{detailInfo.city}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera Task Type
                </Col>
                <Col span={12}>
                  {detailInfo.taskType}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Test mode
                </Col>
                <Col span={12}>
                  {detailInfo.testMode}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Power Saving Mode
                </Col>
                <Col span={12}>
                  {detailInfo.powerSave}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Firmware Version
                </Col>
                <Col span={12}>{detailInfo.firmwareVersion}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Camera Recording Hours
                </Col>
                <Col span={12}>
                  {detailInfo.recordingHours}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  CCS Mode
                </Col>
                <Col span={12}>
                  {detailInfo.ccsMode}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  CCS Interval
                </Col>
                <Col span={12}>
                  {detailInfo.ccSInterval}
                </Col>
              </Row>
            </DetailCard.Row>
            {/*
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Environment
                </Col>
                <Col span={12}>{detailInfo.environment}</Col>
              </Row>
            </DetailCard.Row>
            */}
          </DetailCard>
          <DetailCard icon={<CloudUploadOutlined />} title='Upload' id='upload' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Upload Format
                </Col>
                <Col span={12}>
                  {detailInfo.uploadFormat}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Upload Quality
                </Col>
                <Col span={12}>
                  {detailInfo.uploadQuality}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Image Quality
                </Col>
                <Col span={12}>
                  {detailInfo.imageQuality}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Saver Mode
                </Col>
                <Col span={12}>
                  {detailInfo.dataSaverMode}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Upload Method
                </Col>
                <Col span={12}>
                  {detailInfo.uploadMethod}
                </Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <DetailCard icon={<UserOutlined />} title='User' id='user' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Driver ID
                </Col>
                <Col span={12}>{detailInfo.driverID}</Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <DetailCard icon={<ForkOutlined />} title='AI Models' id='aiModels' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  {'Image Quality'}
                </Col>
                <Col span={12}>
                  {detailInfo.imageQualityOfAiModel}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  {'Privacy'}
                </Col>
                <Col span={12}>
                  {detailInfo.privacy}
                </Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          {/*)}*/}
          <DetailCard icon={<BarChartOutlined />} title='DataQuota' id='dataQuota' className='detail-item'>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Monthly Data Quota
                </Col>
                <Col span={12}>{detailInfo.monthlyDataQuota}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Management Cycle
                </Col>
                <Col span={12}>
                  {detailInfo.dataManagementCycle}
                </Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Alert Limit
                </Col>
                <Col span={12}>{detailInfo.dataAlertLimit}</Col>
              </Row>
            </DetailCard.Row>
            <DetailCard.Row>
              <Row align='middle'>
                <Col span={12} className='title'>
                  Data Hard Limit
                </Col>
                <Col span={12}>{detailInfo.dataHardLimit}</Col>
              </Row>
            </DetailCard.Row>
          </DetailCard>
          <div style={{ height: 800 }}></div>
        </div>
      </>
    )
  }

  const Detail = () => {
    return (
      <div>
        <Header />
        <Content />
      </div>
    )
  }
  return (
    <div className={containerStyle}>
      {visible ? <Detail /> : null}
    </div>
  )
}

interface IDeviceProps {
  deviceID: string,
  visible: boolean,
}
const Container = (props) => {
  const { directory, city: { cityMap }, cameras: {selectedCamera} } = useSelector(({ directory, city,cameras }) => ({ directory, city ,cameras}))
  const { directory: directoryAction, cameras:{updateSelectedCamera,updateUploadingCamera} } = useDispatch()
  const onChange = (action)=>{
    const {type, payload} = action
    switch (type){
      case 'close':
        updateSelectedCamera(null)
        break
      case 'upload':
        updateUploadingCamera(payload)
        break
      default:break

    }
  }
  useEffect(() => {
    if (!selectedCamera)  return
    directoryAction.fetchDetail(selectedCamera).catch(err => {
      if (err.response.status === 404) {
        Modal.error({
          content: 'Not found the camera'
        })
      }
    })
  }, [selectedCamera])
  return (
    <DeviceDetail
      visible={selectedCamera !== null}
      data={{ directory, cityMap }}
      onChange={onChange}
    />
  )
}

export {
  Container as default
}
